import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DescriptionItem,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  StatusPill,
  TableWrap,
  Td,
  Textarea,
  Th,
  Toggle,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import {
  IconAlert,
  IconArrowLeft,
  IconBan,
  IconCheck,
  IconPlus,
  IconRefresh,
  IconUser
} from '../../components/admin/icons';
import { formatDate, formatDateTime, formatNumber } from '../../components/admin/labels';
import {
  PLAN_OPTIONS,
  billingLabel,
  billingTone,
  merchantRoleLabel,
  pilotPreviouslyUsed,
  userStatusTone
} from '../../components/admin/plans';
import { fieldErrorsFrom } from '../../utils/formErrors';

/** ISO timestamp -> value for <input type="date">. */
const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');

const emptyUserForm = { name: '', email: '', merchantRole: 'agent', password: '' };

const MerchantDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [userErrors, setUserErrors] = useState({});
  const [addingUser, setAddingUser] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ next: '', confirm: '' });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [savingPassword, setSavingPassword] = useState(false);
  const [seatInfo, setSeatInfo] = useState(null);
  const [seatTarget, setSeatTarget] = useState('');
  const [savingSeats, setSavingSeats] = useState(false);

  const fetchMerchant = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/merchants/${id}`);
      setMerchant(data);
      setForm({
        name: data.name || '',
        email: data.email || '',
        plan: data.plan || 'pilot',
        freeCaseAllowance: data.freeCaseAllowance ?? '',
        usersAllowed: data.seats?.override ?? '',
        accountManager: data.accountManager || '',
        notes: data.notes || '',
        smsForCustomers: data.smsForCustomers !== false,
        trialStartAt: toDateInput(data.trial?.startAt),
        trialEndAt: toDateInput(data.trial?.endAt)
      });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not load this merchant.', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  const fetchSeats = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/merchants/${id}/seats/quote`);
      setSeatInfo(data);
      setSeatTarget(String(data.currentAdditional ?? 0));
    } catch {
      setSeatInfo(null);
    }
  }, [id]);

  useEffect(() => {
    fetchMerchant();
    fetchSeats();
  }, [fetchMerchant, fetchSeats]);

  const handleSave = async () => {
    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim();
    const found = {};
    if (!trimmedName) found.name = 'Enter the merchant name.';
    if (!trimmedEmail) found.email = 'Enter an email address.';
    else if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) found.email = 'Enter a valid email address.';

    // Checked against the full merchant list before saving, the same way the
    // create form does — catches the common case instantly rather than
    // waiting on a round trip. The server still has the authoritative check
    // (updateMerchant) for a clash created by someone else since this list
    // was fetched.
    if ((trimmedName && !found.name) || (trimmedEmail && !found.email)) {
      try {
        const { data: allMerchants } = await api.get('/api/admin/merchants');
        const others = allMerchants.filter((m) => m._id !== id);
        if (trimmedName && !found.name) {
          const nameClash = others.some((m) => m.name?.trim().toLowerCase() === trimmedName.toLowerCase());
          if (nameClash) found.name = 'Another merchant already uses this name.';
        }
        if (trimmedEmail && !found.email) {
          const emailClash = others.some((m) => m.email?.toLowerCase() === trimmedEmail.toLowerCase());
          if (emailClash) found.email = 'Another merchant already uses this email.';
        }
      } catch {
        // Fall through to the server-side check below if the list can't be fetched.
      }
    }

    if (Object.keys(found).length) {
      setEditErrors(found);
      return;
    }
    setEditErrors({});

    setSaving(true);
    try {
      const payload = {
        name: trimmedName,
        email: trimmedEmail,
        plan: form.plan,
        accountManager: form.accountManager,
        notes: form.notes,
        smsForCustomers: form.smsForCustomers,
        freeCaseAllowance: form.freeCaseAllowance === '' ? null : Number(form.freeCaseAllowance),
        usersAllowed: form.usersAllowed === '' ? null : Number(form.usersAllowed),
        trialStartAt: form.trialStartAt || null,
        trialEndAt: form.trialEndAt || null
      };
      await api.patch(`/api/admin/merchants/${id}`, payload);
      await fetchMerchant();
      setEditing(false);
      toast('Merchant updated.', { tone: 'success' });
    } catch (error) {
      setEditErrors(fieldErrorsFrom(error, { fallback: 'Could not save these changes.' }));
    } finally {
      setSaving(false);
    }
  };

  const handleAddUser = async () => {
    const found = {};
    if (!userForm.name.trim()) found.name = 'Enter their name.';
    if (!userForm.email.trim()) found.email = 'Enter an email address.';
    else if (!/^\S+@\S+\.\S+$/.test(userForm.email.trim())) found.email = 'Enter a valid email address.';
    if (!userForm.password) found.password = 'Enter a password.';
    else if (userForm.password.length < 8) found.password = 'Use at least 8 characters.';
    if (Object.keys(found).length) {
      setUserErrors(found);
      return;
    }
    setUserErrors({});

    setAddingUser(true);
    try {
      await api.post(`/api/admin/merchants/${id}/users`, {
        name: userForm.name.trim(),
        email: userForm.email.trim(),
        merchantRole: userForm.merchantRole,
        password: userForm.password
      });
      setShowAddUser(false);
      setUserForm(emptyUserForm);
      await fetchMerchant();
      toast('User added.', { tone: 'success' });
    } catch (error) {
      setUserErrors(fieldErrorsFrom(error, { fallback: 'Could not add this user.' }));
    } finally {
      setAddingUser(false);
    }
  };

  const handleUserStatus = async (user, status) => {
    try {
      await api.patch(`/api/admin/merchant-users/${user._id}`, { status });
      await fetchMerchant();
      toast(`${user.name} ${status === 'disabled' ? 'disabled' : 'enabled'}.`, { tone: 'success' });
    } catch (error) {
      toast(error.response?.data?.error || 'Could not update this user.', { tone: 'error' });
    }
  };

  const handleSaveSeats = async () => {
    setSavingSeats(true);
    try {
      const { data } = await api.patch(`/api/admin/merchants/${id}/seats`, {
        additional: Number(seatTarget)
      });
      await Promise.all([fetchMerchant(), fetchSeats()]);
      toast(
        data.unchanged
          ? 'Seat count unchanged.'
          : `Paid seats updated to ${data.seats.additional}.`,
        { tone: 'success' }
      );
    } catch (error) {
      toast(error.response?.data?.error || 'Could not change the seat count.', { tone: 'error' });
    } finally {
      setSavingSeats(false);
    }
  };

  /** Set or replace a merchant user's password on their behalf. */
  const handleSetPassword = async () => {
    const found = {};
    if (passwordForm.next.length < 8) found.next = 'Use at least 8 characters.';
    if (!passwordForm.confirm) found.confirm = 'Re-enter the password.';
    else if (passwordForm.next !== passwordForm.confirm) {
      found.confirm = 'The two passwords do not match.';
    }
    if (Object.keys(found).length) {
      setPasswordErrors(found);
      return;
    }
    setPasswordErrors({});

    setSavingPassword(true);
    try {
      await api.patch(`/api/admin/merchant-users/${passwordUser._id}`, {
        password: passwordForm.next
      });
      setPasswordUser(null);
      setPasswordForm({ next: '', confirm: '' });
      await fetchMerchant();
      toast('Password updated. They can sign in with it straight away.', { tone: 'success' });
    } catch (error) {
      setPasswordErrors(
        fieldErrorsFrom(error, {
          fieldMap: { password: 'next' },
          fallback: 'Could not set this password.'
        })
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const handleResendInvite = async (user) => {
    try {
      const { data } = await api.post(`/api/admin/merchant-users/${user._id}/invite`);
      if (data.emailed) {
        toast(`Invitation sent to ${user.email}.`, { tone: 'success' });
      } else {
        // Email delivery is not configured — hand the link over directly.
        setInviteLink({ user, link: data.link, expiryHours: data.expiryHours });
      }
      await fetchMerchant();
    } catch (error) {
      toast(error.response?.data?.error || 'Could not send this invitation.', { tone: 'error' });
    }
  };

  const handleRemoveUser = async (user) => {
    const ok = await confirm({
      title: 'Remove user',
      message: `Remove ${user.name} from this merchant's team?`,
      detail: 'Their seat is freed immediately. This cannot be undone.',
      tone: 'danger',
      confirmLabel: 'Remove user'
    });
    if (!ok) return;

    try {
      await api.delete(`/api/admin/merchant-users/${user._id}`);
      await fetchMerchant();
      toast('User removed.', { tone: 'success' });
    } catch (error) {
      toast('Could not remove this user.', { tone: 'error' });
    }
  };

  const handleDeleteMerchant = async () => {
    const ok = await confirm({
      title: 'Delete merchant',
      message: `Permanently delete ${merchant.name}?`,
      detail: 'Their users are deleted too. Merchants with cases cannot be deleted.',
      tone: 'danger',
      confirmLabel: 'Delete merchant'
    });
    if (!ok) return;

    try {
      await api.delete(`/api/admin/merchants/${id}`);
      toast('Merchant deleted.', { tone: 'success' });
      navigate('/admin/merchants');
    } catch (error) {
      toast(error.response?.data?.error || 'Could not delete this merchant.', { tone: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-44" />
        <Card>
          <CardBody className="space-y-4">
            <Skeleton className="h-5 w-56" />
            <Skeleton className="h-24 w-full" />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!merchant) {
    return (
      <Card>
        <EmptyState
          icon={IconAlert}
          title="Merchant not found"
          description="This merchant may have been deleted."
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/admin/merchants')}>
              <IconArrowLeft size={15} />
              Back to merchants
            </Button>
          }
        />
      </Card>
    );
  }

  const seatsUsed = merchant.usersCount ?? 0;
  const seatsAllowed = merchant.usersAllowed ?? 0;
  const seatsFull = seatsAllowed > 0 && seatsUsed >= seatsAllowed;
  // freeCaseAllowance is a pilot-only field, kept as history once a merchant
  // moves to paid billing — it must not resurface as a pay-per-case "allowance"
  // (pay-per-case has none; subscription already sets includedCases itself).
  const casesAllowed =
    merchant.includedCases ?? (merchant.billingModel === 'free' ? merchant.freeCaseAllowance : null);
  const casesUsed = merchant.caseCountThisCycle ?? 0;
  const casePct = casesAllowed > 0 ? Math.min(100, Math.round((casesUsed / casesAllowed) * 100)) : 0;

  return (
    <div className="space-y-6">
      {/* Back + heading */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate('/admin/merchants')}
            className="mb-2 inline-flex items-center gap-1.5 rounded text-sm font-medium text-ink-500 transition-colors hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          >
            <IconArrowLeft size={15} />
            Back to merchants
          </button>
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900">
            {merchant.name}
          </h2>
          <p className="mt-1 text-sm text-ink-500">{merchant.email}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <StatusPill tone={billingTone(merchant)}>{billingLabel(merchant)}</StatusPill>
          <StatusPill
            tone={merchant.isSuspended ? 'danger' : merchant.isActive ? 'success' : 'neutral'}
          >
            {merchant.isSuspended ? 'Suspended' : merchant.isActive ? 'Active' : 'Inactive'}
          </StatusPill>
          {merchant.isTrialActive && <StatusPill tone="info">Trial active</StatusPill>}
          {!merchant.isTrialActive && pilotPreviouslyUsed(merchant) && (
            <StatusPill tone="neutral">Pilot used</StatusPill>
          )}
          <Button variant="ghost" size="sm" onClick={fetchMerchant}>
            <IconRefresh size={15} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Detail / edit */}
      <Card>
        <CardHeader
          title="Merchant details"
          description="Plan, allowances and account management."
          actions={
            editing ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditing(false);
                    setEditErrors({});
                    fetchMerchant();
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} loading={saving}>
                  <IconCheck size={15} />
                  Save changes
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => { setEditErrors({}); setEditing(true); }}>
                Edit merchant
              </Button>
            )
          }
        />
        <CardBody>
          {editing ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {editErrors._general && (
                <div
                  role="alert"
                  className="sm:col-span-2 rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
                >
                  {editErrors._general}
                </div>
              )}
              <Field label="Merchant name" htmlFor="m-name" required error={editErrors.name}>
                <Input
                  id="m-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field label="Email" htmlFor="m-email" required error={editErrors.email}>
                <Input
                  id="m-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <Field label="Plan" htmlFor="m-plan">
                <Select
                  id="m-plan"
                  value={form.plan}
                  onChange={(e) => setForm({ ...form, plan: e.target.value })}
                >
                  {PLAN_OPTIONS.map((plan) => (
                    <option key={plan.value} value={plan.value}>
                      {plan.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Users allowed"
                htmlFor="m-seats"
                hint="Leave empty to use the plan's own seat count."
              >
                <Input
                  id="m-seats"
                  type="number"
                  min="0"
                  placeholder={`Plan default (${merchant.seats?.included ?? 0})`}
                  value={form.usersAllowed}
                  onChange={(e) => setForm({ ...form, usersAllowed: e.target.value })}
                />
              </Field>
              <Field
                label="Free case allowance"
                htmlFor="m-cases"
                hint="Used for pilots. Empty falls back to the plan allowance."
              >
                <Input
                  id="m-cases"
                  type="number"
                  min="0"
                  placeholder="—"
                  value={form.freeCaseAllowance}
                  onChange={(e) => setForm({ ...form, freeCaseAllowance: e.target.value })}
                />
              </Field>
              <Field label="Account manager" htmlFor="m-am">
                <Input
                  id="m-am"
                  value={form.accountManager}
                  onChange={(e) => setForm({ ...form, accountManager: e.target.value })}
                />
              </Field>
              <Field label="Trial start" htmlFor="m-ts">
                <Input
                  id="m-ts"
                  type="date"
                  value={form.trialStartAt}
                  onChange={(e) => setForm({ ...form, trialStartAt: e.target.value })}
                />
              </Field>
              <Field label="Trial end" htmlFor="m-te">
                <Input
                  id="m-te"
                  type="date"
                  value={form.trialEndAt}
                  onChange={(e) => setForm({ ...form, trialEndAt: e.target.value })}
                />
              </Field>
              <div className="sm:col-span-2">
                <Toggle
                  id="m-sms"
                  label="SMS for customers"
                  description="Customers may buy SMS updates on this merchant's cases (the customer pays)."
                  checked={form.smsForCustomers}
                  onChange={(checked) => setForm({ ...form, smsForCustomers: checked })}
                />
              </div>
              <Field label="Notes" htmlFor="m-notes" className="sm:col-span-2">
                <Textarea
                  id="m-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Internal notes — not shown to the merchant."
                />
              </Field>
            </div>
          ) : (
            <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <DescriptionItem label="Plan">
                {billingLabel(merchant)}
                {pilotPreviouslyUsed(merchant) && (
                  <span className="ml-1.5 text-xs text-ink-500">(pilot previously used)</span>
                )}
              </DescriptionItem>
              <DescriptionItem label="Trial start">
                {merchant.trial?.startAt ? formatDate(merchant.trial.startAt) : '—'}
              </DescriptionItem>
              <DescriptionItem label="Trial end">
                {merchant.trial?.endAt ? formatDate(merchant.trial.endAt) : '—'}
              </DescriptionItem>
              <DescriptionItem label="Free case allowance">
                {merchant.freeCaseAllowance != null ? formatNumber(merchant.freeCaseAllowance) : '—'}
              </DescriptionItem>
              <DescriptionItem label="Users allowed">
                {formatNumber(seatsAllowed)}
                {merchant.seats?.override != null && (
                  <span className="ml-1.5 text-xs text-ink-500">(override)</span>
                )}
              </DescriptionItem>
              <DescriptionItem label="Billing model">
                {merchant.billingModel === 'pay_per_case'
                  ? 'Pay per case'
                  : merchant.billingModel === 'subscription'
                    ? 'Subscription'
                    : 'Free'}
              </DescriptionItem>
              <DescriptionItem label="SMS for customers">
                {merchant.smsForCustomers !== false ? 'Enabled (customer pays)' : 'Disabled'}
              </DescriptionItem>
              <DescriptionItem label="Account manager">
                {merchant.accountManager || '—'}
              </DescriptionItem>
              <DescriptionItem label="Total cases">
                {formatNumber(merchant.totalCases ?? 0)}
              </DescriptionItem>
              <div className="sm:col-span-3">
                <dt className="text-xs font-medium uppercase tracking-wide text-ink-500">
                  Cases used {(casesAllowed != null || merchant.billingModel === 'subscription') && `(this cycle)`}
                </dt>
                <dd className="mt-2">
                  {casesAllowed != null ? (
                    <>
                      <p className="text-sm tabular-nums text-ink-900">
                        <span className="font-600">{formatNumber(casesUsed)}</span>
                        <span className="text-ink-500"> / {formatNumber(casesAllowed)}</span>
                      </p>
                      <div className="mt-1.5 h-2 w-full max-w-md overflow-hidden rounded-full bg-ink-100">
                        <div
                          className={`h-full rounded-[4px] ${
                            casesUsed > casesAllowed ? 'bg-warning-600' : 'bg-brand-600'
                          }`}
                          style={{ width: `${casePct}%` }}
                        />
                      </div>
                    </>
                  ) : merchant.billingModel === 'subscription' ? (
                    <p className="text-sm text-ink-900">
                      <span className="font-600">{formatNumber(casesUsed)}</span>
                      <span className="text-ink-500"> / Unlimited</span>
                    </p>
                  ) : (
                    <p className="text-sm text-ink-500">No allowance set</p>
                  )}
                </dd>
              </div>
              {merchant.notes && (
                <div className="sm:col-span-3">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-500">Notes</dt>
                  <dd className="mt-1.5 whitespace-pre-wrap rounded-lg bg-ink-50 px-3.5 py-3 text-sm text-ink-700">
                    {merchant.notes}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </CardBody>
      </Card>

      {/* Paid seats (billed via Stripe) */}
      <Card>
        <CardHeader
          title="Paid seats"
          description="Extra user seats charged on the merchant's Stripe subscription, on top of the seats their plan includes."
        />
        <CardBody>
          {seatInfo ? (
            <>
              <dl className="grid grid-cols-1 gap-5 sm:grid-cols-4">
                <DescriptionItem label="Included with plan">
                  {formatNumber(seatInfo.includedSeats)}
                </DescriptionItem>
                <DescriptionItem label="Paid extra seats">
                  {formatNumber(seatInfo.currentAdditional)}
                </DescriptionItem>
                <DescriptionItem label="Seats in use">
                  {formatNumber(seatInfo.seatsInUse)}
                </DescriptionItem>
                <DescriptionItem label="Current seat cost">
                  £{Number(seatInfo.currentMonthly).toFixed(2)}/mo
                </DescriptionItem>
              </dl>

              {!seatInfo.billable && (
                <div className="mt-5 flex items-start gap-3 rounded-xl bg-warning-50 px-4 py-3.5 ring-1 ring-inset ring-warning-100">
                  <IconAlert size={17} className="mt-0.5 shrink-0 text-warning-600" />
                  <div className="min-w-0">
                    <p className="text-sm text-warning-700">{seatInfo.billableReason}</p>
                    <p className="mt-1 text-xs text-warning-700/80">
                      Use “Users allowed” above to grant seats without charging — that override is
                      deliberately not billed.
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-ink-100 pt-5">
                <Field
                  label="Paid extra seats"
                  htmlFor="seat-target"
                  hint={`At least ${seatInfo.minAdditional} needed for the users already active.`}
                >
                  <Input
                    id="seat-target"
                    type="number"
                    min={seatInfo.minAdditional}
                    value={seatTarget}
                    onChange={(e) => setSeatTarget(e.target.value)}
                    disabled={!seatInfo.billable}
                    className="w-32"
                  />
                </Field>
                <Button
                  onClick={handleSaveSeats}
                  loading={savingSeats}
                  disabled={
                    !seatInfo.billable || String(seatInfo.currentAdditional) === String(seatTarget)
                  }
                >
                  <IconCheck size={16} />
                  Update seats
                </Button>
                {seatInfo.billable && String(seatInfo.currentAdditional) !== String(seatTarget) && (
                  <p className="pb-2 text-sm text-ink-600">
                    {Number(seatTarget) > seatInfo.currentAdditional ? '+' : ''}
                    £
                    {Math.abs(
                      (Number(seatTarget) - seatInfo.currentAdditional) * seatInfo.seatPrice
                    ).toFixed(2)}
                    /month, prorated on the next invoice
                  </p>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-ink-500">Seat information unavailable.</p>
          )}
        </CardBody>
      </Card>

      {/* Users / seats */}
      <Card>
        <CardHeader
          title={`Users (${seatsUsed}/${seatsAllowed} seats used)`}
          description="The merchant's own team. Admins manage users; agents work cases."
          actions={
            <Button size="sm" onClick={() => setShowAddUser(true)} disabled={seatsFull}>
              <IconPlus size={15} />
              Add user
            </Button>
          }
        />

        {seatsFull && (
          <div className="flex items-start gap-3 border-b border-ink-200 bg-warning-50 px-5 py-3.5">
            <IconAlert size={17} className="mt-0.5 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-700">
              Every seat is in use. Raise “Users allowed” or move the merchant to a larger plan
              before adding another user.
            </p>
          </div>
        )}

        {merchant.users?.length ? (
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Last login</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {merchant.users.map((user) => (
                <Tr key={user._id}>
                  <Td className="font-600 text-ink-900">{user.name}</Td>
                  <Td className="text-ink-600">{user.email}</Td>
                  <Td>{merchantRoleLabel(user.merchantRole)}</Td>
                  <Td>
                    <StatusPill tone={userStatusTone(user.status)}>{user.status}</StatusPill>
                  </Td>
                  <Td className="text-ink-500">
                    {user.lastLogin ? formatDateTime(user.lastLogin) : 'Never'}
                  </Td>
                  <Td className="text-right">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {user.status !== 'disabled' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setPasswordUser(user);
                            setPasswordForm({ next: '', confirm: '' });
                            setPasswordErrors({});
                          }}
                        >
                          Set password
                        </Button>
                      )}
                      {user.status !== 'disabled' && (
                        <Button variant="secondary" size="sm" onClick={() => handleResendInvite(user)}>
                          {user.status === 'invited' ? 'Resend invite' : 'Send reset'}
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          handleUserStatus(user, user.status === 'disabled' ? 'active' : 'disabled')
                        }
                      >
                        {user.status === 'disabled' ? <IconCheck size={15} /> : <IconBan size={15} />}
                        {user.status === 'disabled' ? 'Enable' : 'Disable'}
                      </Button>
                      <Button variant="dangerGhost" size="sm" onClick={() => handleRemoveUser(user)}>
                        Remove
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        ) : (
          <EmptyState
            icon={IconUser}
            title="No users yet"
            description="Add the merchant's support team so they can work cases on their own."
            action={
              <Button size="sm" onClick={() => setShowAddUser(true)}>
                <IconPlus size={15} />
                Add user
              </Button>
            }
          />
        )}
      </Card>

      {/* Danger zone */}
      <Card>
        <CardHeader title="Danger zone" description="Deleting a merchant cannot be undone." />
        <CardBody>
          <Button variant="danger" size="sm" onClick={handleDeleteMerchant}>
            Delete merchant
          </Button>
          <p className="mt-2 text-xs text-ink-500">
            Merchants with cases are protected — deactivate or suspend them instead.
          </p>
        </CardBody>
      </Card>

      {/* Set a password on a user's behalf */}
      <Modal
        open={Boolean(passwordUser)}
        onClose={() => setPasswordUser(null)}
        title="Set password"
        description={passwordUser ? `${passwordUser.name} · ${passwordUser.email}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPasswordUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleSetPassword} loading={savingPassword}>
              Set password
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {passwordErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {passwordErrors._general}
            </div>
          )}

          <Field
            label="New password"
            htmlFor="sp-next"
            required
            error={passwordErrors.next}
            hint="At least 8 characters. Share it with them securely."
          >
            <Input
              id="sp-next"
              type="text"
              value={passwordForm.next}
              onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
              autoComplete="new-password"
              autoFocus
            />
          </Field>

          <Field label="Confirm password" htmlFor="sp-confirm" required error={passwordErrors.confirm}>
            <Input
              id="sp-confirm"
              type="text"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              autoComplete="new-password"
            />
          </Field>

          <p className="text-xs leading-relaxed text-ink-500">
            Setting a password activates an invited user and cancels any outstanding invitation or
            reset link.
          </p>
        </div>
      </Modal>

      {/* Invitation link, when email delivery is unavailable */}
      <Modal
        open={Boolean(inviteLink)}
        onClose={() => setInviteLink(null)}
        title="Invitation link"
        description={
          inviteLink
            ? `Email delivery is not configured, so send this link to ${inviteLink.user.email} yourself.`
            : ''
        }
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setInviteLink(null)}>
            Done
          </Button>
        }
      >
        <Input
          readOnly
          value={inviteLink?.link || ''}
          onClick={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <p className="mt-2 text-xs text-ink-500">
          Valid for {inviteLink?.expiryHours} hours, and can be used once.
        </p>
      </Modal>

      {/* Add user */}
      <Modal
        open={showAddUser}
        onClose={() => {
          setShowAddUser(false);
          setUserErrors({});
        }}
        title="Add user"
        description={`${seatsUsed} of ${seatsAllowed} seats used.`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddUser(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} loading={addingUser}>
              Add user
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {userErrors._general && (
            <div
              role="alert"
              className="rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-700 ring-1 ring-inset ring-danger-100"
            >
              {userErrors._general}
            </div>
          )}

          <Field label="Name" htmlFor="u-name" required error={userErrors.name}>
            <Input
              id="u-name"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              autoFocus
            />
          </Field>

          <Field label="Email" htmlFor="u-email" required error={userErrors.email}>
            <Input
              id="u-email"
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
            />
          </Field>

          <Field label="Role" htmlFor="u-role">
            <Select
              id="u-role"
              value={userForm.merchantRole}
              onChange={(e) => setUserForm({ ...userForm, merchantRole: e.target.value })}
            >
              <option value="agent">Agent — works cases</option>
              <option value="admin">Admin — also manages users</option>
            </Select>
          </Field>

          <Field label="Initial password" htmlFor="u-pass" required error={userErrors.password}>
            <Input
              id="u-pass"
              type="text"
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              placeholder="Minimum 8 characters"
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
};

export default MerchantDetail;
